import { UpdatesConfig } from "@urql/exchange-graphcache";

import {
  DeleteCanvasObjectsResult,
  MutationArchiveAssetArgs,
  MutationCreateTrainingRunArgs,
  MutationDeleteAssetArgs,
  MutationDeleteCanvasObjectsArgs,
  MutationDeleteDirectoryArgs,
  MutationDeleteDriveItemsArgs,
  MutationDeleteObjectsArgs,
  MutationDeletePersonalAccessTokenArgs,
  MutationDeleteReferenceSetArgs,
  MutationDeleteStyleArgs,
  MutationDeleteWorkspaceIntegrationArgs,
  MutationRemoveReferenceSetFinetuneArgs,
  MutationRestoreCanvasObjectsArgs,
  MutationTransferCreativeUnitsArgs,
  MutationUpdateReferenceSetArgs,
  MutationUpdateStyleArgs,
  RestoreCanvasObjectsResult,
} from "../schema.graphql";

export const updatesConfig: Partial<UpdatesConfig> = {
  Mutation: {
    transferCreativeUnits: (
      result,
      { input }: MutationTransferCreativeUnitsArgs,
      cache,
    ) => {
      // Invalidate both workspaces so their balances and creativeUnitTransfers
      // history refetch after a transfer.
      cache.invalidate({
        __typename: "Workspace",
        id: input.sourceWorkspaceId,
      });
      cache.invalidate({
        __typename: "Workspace",
        id: input.targetWorkspaceId,
      });
    },
    deleteStyle: (result, { input }: MutationDeleteStyleArgs, cache) => {
      if (input.styleId) {
        cache.invalidate({ __typename: "Style", id: input.styleId });
        cache.invalidate({ __typename: "ReferenceSet", id: input.styleId });
      }
    },
    updateStyle: (result, { input }: MutationUpdateStyleArgs, cache) => {
      const isStatusUpdate = input.styleId && input.status;

      if (isStatusUpdate) {
        cache.invalidate({ __typename: "Style", id: input.styleId });
        cache.invalidate({ __typename: "ReferenceSet", id: input.styleId });
      }
    },
    deleteReferenceSet: (
      result,
      { input }: MutationDeleteReferenceSetArgs,
      cache,
    ) => {
      if (input.referenceSetId) {
        cache.invalidate({
          __typename: "Style",
          id: input.referenceSetId,
        });
        cache.invalidate({
          __typename: "ReferenceSet",
          id: input.referenceSetId,
        });
      }
    },
    updateReferenceSet: (
      result,
      { input }: MutationUpdateReferenceSetArgs,
      cache,
    ) => {
      const isStatusUpdate = input.referenceSetId && input.status;

      if (isStatusUpdate) {
        cache.invalidate({
          __typename: "Style",
          id: input.referenceSetId,
        });
        cache.invalidate({
          __typename: "ReferenceSet",
          id: input.referenceSetId,
        });
      }
    },
    removeReferenceSetFinetune: (
      result,
      { input }: MutationRemoveReferenceSetFinetuneArgs,
      cache,
    ) => {
      // Detaching a LoRA changes the set's loraAssets, so invalidate the
      // ReferenceSet to force a refetch.
      cache.invalidate({
        __typename: "ReferenceSet",
        id: input.referenceSetId,
      });
    },
    deleteDriveItems: (
      result,
      { input }: MutationDeleteDriveItemsArgs,
      cache,
    ) => {
      input.fileIds?.forEach((fileId) =>
        cache.invalidate({ __typename: "File", id: fileId }),
      );
      input.directoryIds?.forEach((directoryId) =>
        cache.invalidate({ __typename: "Directory", id: directoryId }),
      );
      input.styleIds?.forEach((styleId) => {
        cache.invalidate({ __typename: "Style", id: styleId });
        cache.invalidate({ __typename: "ReferenceSet", id: styleId });
      });
      input.assetIds?.forEach((assetId) =>
        cache.invalidate({ __typename: "Asset", id: assetId }),
      );
    },
    deleteObjects: (result, { input }: MutationDeleteObjectsArgs, cache) => {
      input.objectIds.forEach((objectId) => {
        cache.invalidate({ __typename: "File", id: objectId });
        cache.invalidate({ __typename: "Asset", id: objectId });
        cache.invalidate({ __typename: "Style", id: objectId });
        cache.invalidate({ __typename: "ReferenceSet", id: objectId });
      });
    },
    deleteCanvasObjects: (
      result: { deleteCanvasObjects?: DeleteCanvasObjectsResult | null },
      _: MutationDeleteCanvasObjectsArgs,
      cache,
    ) => {
      result.deleteCanvasObjects?.deletedIds?.forEach((id) =>
        cache.invalidate({ __typename: "SessionCanvasObject", id }),
      );
    },
    restoreCanvasObjects: (
      result: { restoreCanvasObjects?: RestoreCanvasObjectsResult | null },
      _: MutationRestoreCanvasObjectsArgs,
      cache,
    ) => {
      result.restoreCanvasObjects?.canvasObjects?.forEach(({ id }) =>
        cache.invalidate({ __typename: "SessionCanvasObject", id }),
      );
    },
    deleteDirectory: (
      result,
      { input }: MutationDeleteDirectoryArgs,
      cache,
    ) => {
      cache.invalidate({ __typename: "Directory", id: input.directoryId });
    },
    deleteAsset: (result, { input }: MutationDeleteAssetArgs, cache) => {
      cache.invalidate({ __typename: "Asset", id: input.assetId });
    },
    archiveAsset: (result, { input }: MutationArchiveAssetArgs, cache) => {
      cache.invalidate({ __typename: "Asset", id: input.assetId });
    },
    deletePersonalAccessToken: (
      result,
      { input }: MutationDeletePersonalAccessTokenArgs,
      cache,
    ) => {
      cache.invalidate({
        __typename: "PersonalAccessToken",
        id: input.personalAccessTokenId,
      });
    },
    deleteWorkspaceIntegration: (
      result,
      { input }: MutationDeleteWorkspaceIntegrationArgs,
      cache,
    ) => {
      cache.invalidate({
        __typename: "WorkspaceIntegration",
        id: input.workspaceIntegrationId,
      });
    },
    createTrainingRun: (
      result,
      { input }: MutationCreateTrainingRunArgs,
      cache,
    ) => {
      cache.invalidate({
        __typename: "Style",
        id: input.styleId,
      });
    },
  },
  Query: {},
};
